import { useTranslation } from "react-i18next";
import { ReactComponent as PencilOutlined } from "assets/icons/pencilOutlined.svg";
import { OffsiteCampaignContextType } from "context/OffsiteCampaignProvider";
import RetailFormInfo from "components/Form/RetailFormInfo";
import RetailFormRow from "components/Form/RetailFormRow";
import RetailText from "components/Typography/RetailText";
import cm from "./style.module.scss";

interface OffsiteAdPreviewProps {
  formFields: OffsiteCampaignContextType["formFields"];
  selectedProducts: Array<any>;
  audienceOptions: Array<{ value: string; label: string }>;
  openEditDrawer: () => void;
}

const OffsiteAdPreview = ({
  formFields,
  selectedProducts,
  audienceOptions,
  openEditDrawer,
}: OffsiteAdPreviewProps) => {
  const { t } = useTranslation();

  return (
    <RetailFormRow className="form-row">
      <RetailFormInfo
        column={{
          number: "5",
          title: t("components.offsiteCampaignForm.firstStep.adPreview"),
          text: t("components.offsiteCampaignForm.firstStep.adPreviewText"),
        }}
      />
      <section className={cm.previewContainer}>
        <article className={cm.previewCard}>
          <div className={cm.previewHeader}>
            <RetailText weight="bold" size="xxxs" className={cm.previewTitle}>
              {formFields.adName ||
                t(
                  "components.offsiteCampaignForm.firstStep.pleaseSelectProduct"
                )}
            </RetailText>
          </div>

          <div className={cm.fieldWithImage}>
            <div className={cm.fieldLabelContainer}>
              <RetailText weight="medium" size="xxxs" className={cm.fieldLabel}>
                {`${t(
                  "components.offsiteCampaignForm.firstStep.productName"
                )}:`}
              </RetailText>
              <RetailText weight="medium" size="xxxs" className={cm.fieldValue}>
                {selectedProducts[0]?.name || "-"}
              </RetailText>
            </div>
            {selectedProducts[0]?.image_url && (
              <img
                src={selectedProducts[0].image_url}
                alt="Product preview"
                className={cm.thumbnailImage}
              />
            )}
          </div>

          <div className={cm.field}>
            <RetailText weight="medium" size="xxxs" className={cm.fieldLabel}>
              {`${t("components.offsiteCampaignForm.firstStep.destination")}:`}
            </RetailText>
            <a
              href={formFields.redirectUrl}
              target="_blank"
              rel="noreferrer"
              className={cm.fieldLink}
              onClick={(e) =>
                formFields.redirectUrl === "" && e.preventDefault()
              }
            >
              {formFields.redirectUrl || "-"}
            </a>
          </div>

          <div className={cm.field}>
            <RetailText weight="medium" size="xxxs" className={cm.fieldLabel}>
              {`${t("components.offsiteCampaignForm.firstStep.displayLink")}:`}
            </RetailText>
            <RetailText size="xxxs" weight="medium" className={cm.fieldValue}>
              {selectedProducts[0]?.name || "-"}
            </RetailText>
          </div>

          <div className={cm.field}>
            <RetailText weight="medium" size="xxxs" className={cm.fieldLabel}>
              {`${t("components.offsiteCampaignForm.firstStep.primaryText")}:`}
            </RetailText>
            <RetailText size="xxxs" weight="medium" className={cm.fieldValue}>
              {formFields.primaryText || "-"}
            </RetailText>
          </div>

          <div className={cm.field}>
            <RetailText weight="medium" size="xxxs" className={cm.fieldLabel}>
              {`${t("components.offsiteCampaignForm.firstStep.headline")}:`}
            </RetailText>
            <RetailText size="xxxs" weight="medium" className={cm.fieldValue}>
              {formFields.headline || "-"}
            </RetailText>
          </div>

          <div className={cm.field}>
            <RetailText weight="medium" size="xxxs" className={cm.fieldLabel}>
              {`${t("components.offsiteCampaignForm.firstStep.description")}:`}
            </RetailText>
            <RetailText size="xxxs" weight="medium" className={cm.fieldValue}>
              {formFields.description || "-"}
            </RetailText>
          </div>

          <div className={cm.field}>
            <RetailText weight="medium" size="xxxs" className={cm.fieldLabel}>
              {`${t("components.offsiteCampaignForm.firstStep.audience")}:`}
            </RetailText>
            <RetailText size="xxxs" weight="medium" className={cm.fieldValue}>
              {formFields.audience
                ? audienceOptions.find(
                    (option) => option.value === formFields.audience
                  )?.label || formFields.audience
                : "-"}
            </RetailText>
          </div>

          <div className={cm.field}>
            <RetailText weight="medium" size="xxxs" className={cm.fieldLabel}>
              {t("components.offsiteCampaignForm.firstStep.cta")}
            </RetailText>
            <RetailText size="xxxs" weight="medium" className={cm.fieldValue}>
              {t("components.offsiteCampaignForm.firstStep.fixedCta")}
            </RetailText>
          </div>
          <div className={cm.previewFooter}>
            {selectedProducts?.length > 0 && (
              <button
                className={cm.editButton}
                type="button"
                onClick={openEditDrawer}
              >
                <PencilOutlined />
              </button>
            )}
          </div>
        </article>
      </section>
    </RetailFormRow>
  );
};

export default OffsiteAdPreview;
